import { Controller } from '@hotwired/stimulus';
import { fetchData } from '@shared/script/fetchData';
import { wishlistStore, wishlistRetryStore, wishlistRetryContext } from '@site-module/store/wishlist';
import { useStore } from '@store/hooks/useStore';
import { isWishlistResponseMap } from './wishlist_add_button_controller';
import { CustomEventNames, dispatchEvent } from '@shared/script/eventDispatcher';
import type { StoreController } from '@store/types/storeController';
import type { WishlistAddResponse } from './wishlist_add_button_controller';

type WishlistRetryPayload = { id?: string, title?: string };

export default class WishlistCountController extends Controller implements StoreController {
    static stores = [wishlistStore, wishlistRetryStore, wishlistRetryContext];

    // Wherever this is hooked up, we need both the standard and order send endpoints
    static values = {
        retrieveEndpoint: String,
        sendEndpoint: String,
        orderSendEndpoint: String,
        loggedIn: Boolean
    };

    static targets = ['counter'];

    declare loggedInValue: boolean;
    declare sendEndpointValue: string;
    declare orderSendEndpointValue: string;
    declare retrieveEndpointValue: string;
    declare counterTargets: HTMLSpanElement[];
    declare wishlistValue: number[];
    declare wishlistRetryValue: WishlistRetryPayload;
    declare wishlistRetryContextValue: string;
    declare setWishlistRetryValue: (retry: WishlistRetryPayload) => void;
    declare setWishlistRetryContextValue: (context: string) => void;
    declare setWishlistValue: (wishlist: number[]) => void;
    declare onWishlistUpdate: () => void;

    fetchFn: typeof fetchData = fetchData;

    async connect(): Promise<void> {
        useStore(this);
        if (this.loggedInValue === true) {
            await this.retrieveWishlist();
            // Our retry cookie will be set here where the user tried to add before logging in
            if (Object.keys(this.wishlistRetryValue).length > 0) {
                await this.applyRetryIfExists();
            }
        }
    }

    async retrieveWishlist(): Promise<void> {
        const result = await this.fetchFn(this.retrieveEndpointValue);

        if (result.ok) {
            this.setWishlistValue(result.data);
        }
    }

    async applyRetryIfExists(): Promise<void> {
        const retry = this.wishlistRetryValue as WishlistRetryPayload;

        if (typeof retry?.id === 'undefined') {
            // eslint-disable-next-line no-console
            console.warn('No retry ID found');
            return;
        }

        const payload = new URLSearchParams();
        payload.append('id', retry.id);

        // If we were in the order context when the retry was set, the one shot cookie will be present
        // we use this to determine which endpoint to try and send to.
        // We also reload on success in this context as the order page will need to update.
        const endpoint = this.wishlistRetryContextValue === 'order' ? this.orderSendEndpointValue : this.sendEndpointValue;
        const reloadOnSuccess = this.wishlistRetryContextValue === 'order';

        const result = await this.fetchFn<WishlistAddResponse>(endpoint, 'POST', {
            body: payload
        });

        if (result.ok && isWishlistResponseMap(result.data)) {
            const ids = Object.entries(result.data)
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .filter(([_, added]) => added === true)
                .map(([id]) => Number(id));

            if (reloadOnSuccess) {
                window.location.reload();
            } else {
                dispatchEvent(CustomEventNames.WishlistModalOpen, {
                    modalId: 'wishlistModal',
                    title: retry?.title ?? ''
                });
            }

            this.setWishlistValue([...this.wishlistValue, ...ids]);
        }

        this.setWishlistRetryContextValue('');
        this.setWishlistRetryValue({});
    }

    onWishlistUpdate(): void {
        const count = this.wishlistValue?.length ?? 0;

        if (count > 0) {
            this.counterTargets.forEach((target) => target.innerText = `(${count})`);
        } else {
            this.counterTargets.forEach((target) => target.innerText = null);
        }
    }
}
